import { useEffect, useRef, useState } from 'react';
import { Minus, Plus, Play, Square } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { getEngine, useEngineRunning } from '@/lib/engines';
import { findSig } from '@/lib/timeSignatures';
import { clickBus } from '@/lib/clickBus';

const SIGS = ['2/4', '3/4', '4/4', '5/4', '6/8', '7/8'];
const clamp = (v) => Math.max(30, Math.min(300, Math.round(v) || 80));
const load = () => {
  let saved = {};
  try { saved = JSON.parse(localStorage.getItem('tuner.click') || '{}'); } catch { /* defaults */ }
  return { sig: '4/4', vol: 0.5, ...saved, bpm: clamp(saved.bpm ?? 80) };
};
const stepBtn = 'h-10 w-10 shrink-0 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/5 active:scale-95 transition-transform';

export const TunerClick = () => {
  const [cfg, setCfg] = useState(load);
  const [bpmText, setBpmText] = useState(() => String(cfg.bpm));
  useEffect(() => { setBpmText(String(cfg.bpm)); }, [cfg.bpm]);
  const [beat, setBeat] = useState(-1);
  const [countIn, setCountIn] = useState(0);
  const engineRef = useRef(null);

  if (!engineRef.current) engineRef.current = getEngine('click');
  const playing = useEngineRunning(engineRef.current);
  const pattern = findSig(cfg.sig).pattern;

  // Shared engine: keeps clicking across pages / locked screen until stopped.
  useEffect(() => {
    const e = engineRef.current;
    e.onBeat = (n) => {
      setBeat(n.beatInBar ?? -1);
      setCountIn(n.isCountIn ? n.countInBarsLeft || 1 : 0);
      if (n.beatInBar === 0 && !n.isCountIn) clickBus.emitBar();
    };
    const begin = (bars) => { e.setCountInBars(bars); e.start(); };
    clickBus.control = {
      start: () => { if (!e.isRunning()) { begin(0); return true; } return false; },
      setBpm: (bpm) => { const b = clamp(bpm); e.setBpm(b); setCfg((c) => ({ ...c, bpm: b })); },
      // Restart at a given BPM with N count-in bars (drone chord Play).
      startWith: ({ bpm, countIn: bars = 0 }) => {
        if (bpm) { const b = clamp(bpm); e.setBpm(b); setCfg((c) => ({ ...c, bpm: b })); }
        if (e.isRunning()) e.stop();
        begin(bars);
      },
    };
    const off = e.onRunning((r) => { if (!r) { setBeat(-1); setCountIn(0); clickBus.emitStop(); } });
    return () => { off(); e.onBeat = null; clickBus.control = null; };
  }, []);

  useEffect(() => {
    const e = engineRef.current;
    e.setBpm(cfg.bpm);
    e.setPattern(pattern);
    e.setVolume(cfg.vol);
    localStorage.setItem('tuner.click', JSON.stringify(cfg));
  }, [cfg, pattern]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const toggle = () => {
    const e = engineRef.current;
    if (e.isRunning()) e.stop(); else { e.setCountInBars(0); e.start(); }
  };

  return (
    <section data-testid="tuner-click" className="bg-[#111112] border border-white/5 rounded-2xl p-4 sm:p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Click · studia con il tempo</span>
        {playing && countIn > 0 && <span data-testid="tuner-click-countin" className="text-[10px] uppercase tracking-[0.2em] text-amber-300 animate-pulse">Conto · {countIn}</span>}
        <div className="flex gap-1">
          {pattern.map((_, i) => (
            <span key={i} data-testid={`tuner-click-beat-${i}`} className={`w-2 h-2 rounded-full transition-colors duration-75 ${playing && beat === i ? (i === 0 ? 'bg-sky-400' : 'bg-emerald-400') : 'bg-white/10'}`} />
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="tuner-click-toggle" onClick={toggle} aria-label={playing ? 'Stop click' : 'Play click'} className={`h-12 w-12 shrink-0 rounded-full flex items-center justify-center active:scale-95 transition-[transform,background-color] ${playing ? 'bg-rose-500 text-white' : 'bg-pink-400 text-black'}`}>
          {playing ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="tuner-click-bpm-down" aria-label="BPM -1" onClick={() => set({ bpm: clamp(cfg.bpm - 1) })} className={stepBtn}><Minus className="w-4 h-4" /></button>
          <input
            data-testid="tuner-click-bpm"
            type="number"
            inputMode="numeric"
            value={bpmText}
            onChange={(e) => setBpmText(e.target.value)}
            onBlur={() => { const b = clamp(Number(bpmText)); set({ bpm: b }); setBpmText(String(b)); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="w-16 h-10 rounded-lg bg-[#0A0A0A] border border-white/10 text-center font-bebas text-2xl focus:outline-none focus:border-pink-400"
          />
          <button data-testid="tuner-click-bpm-up" aria-label="BPM +1" onClick={() => set({ bpm: clamp(cfg.bpm + 1) })} className={stepBtn}><Plus className="w-4 h-4" /></button>
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">BPM</span>
        </div>
        <select data-testid="tuner-click-sig" value={cfg.sig} onChange={(e) => set({ sig: e.target.value })} className="h-10 rounded-lg bg-[#0A0A0A] border border-white/10 px-3 text-sm focus:outline-none focus:border-pink-400">
          {SIGS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 w-16">Volume</span>
        <Slider data-testid="tuner-click-volume" value={[cfg.vol * 100]} min={0} max={100} step={1} onValueChange={(v) => set({ vol: v[0] / 100 })} className="flex-1" />
        <span data-testid="tuner-click-volume-value" className="w-10 text-right font-mono text-xs text-white/50">{Math.round(cfg.vol * 100)}%</span>
      </div>
    </section>
  );
};
